import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import {
  ANTECEDENT_OPTIONS,
  BEHAVIOR_TYPE_OPTIONS,
  CONSEQUENCE_OPTIONS,
  FUNCTIONS_OF_BEHAVIOR,
  FUNCTION_BY_VALUE,
  INTERVENTION_OPTIONS,
  LOCATION_OPTIONS,
  SETTING_ACTIVITIES,
  STUDENT_REACTION_OPTIONS,
} from '../../../lib/pbisConstants';
import {
  useUserProfiles,
  shortRoleLabel,
  roleColorClasses,
} from 'context/UserProfilesContext';
import { deriveNameFromEmail } from 'lib/currentUser';
import { downloadIncidentsCsv } from 'lib/behaviorIncidentsCsv';

// The three ABC banks + FBA vocabulary are all stored as short "kebab-value"
// enums (see pbisConstants.js). For the table we want the friendly English
// label — "Task / demand given" instead of "task-demand" — so a
// case-manager reading the printed report doesn't have to translate.
const labelLookup = (options) =>
  Object.fromEntries((options || []).map((o) => [o.value, o.label]));

const LOOKUPS = {
  antecedent: labelLookup(ANTECEDENT_OPTIONS),
  behavior: labelLookup(BEHAVIOR_TYPE_OPTIONS),
  consequence: labelLookup(CONSEQUENCE_OPTIONS),
  location: labelLookup(LOCATION_OPTIONS),
  setting: labelLookup(SETTING_ACTIVITIES),
  studentReaction: labelLookup(STUDENT_REACTION_OPTIONS),
  intervention: labelLookup(INTERVENTION_OPTIONS),
};

const humanize = (kind, value) => {
  if (value === null || value === undefined || value === '') return '';
  return LOOKUPS[kind]?.[value] || String(value).replace(/-/g, ' ');
};

const behaviorTypeFilters = [
  { value: 'all', label: 'All Behaviors' },
  ...BEHAVIOR_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
];

const severityFilters = [
  { value: 'all', label: 'All Severities' },
  { value: 'low', label: 'Low (1-2)' },
  { value: 'medium', label: 'Medium (3)' },
  { value: 'high', label: 'High (4-5)' },
];

const functionFilters = [
  { value: 'all', label: 'All Functions' },
  ...FUNCTIONS_OF_BEHAVIOR.map((f) => ({ value: f.value, label: f.label })),
];

const followUpFilters = [
  { value: 'all', label: 'All Incidents' },
  { value: 'yes', label: 'Needs follow-up' },
  { value: 'no', label: 'No follow-up needed' },
];

const sortOptions = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'severity-desc', label: 'Highest intensity' },
  { value: 'severity-asc', label: 'Lowest intensity' },
  { value: 'duration-desc', label: 'Longest duration' },
];

const timeOf = (i) => new Date(i?.occurredAt || i?.timestamp || 0);

const formatDate = (i) =>
  timeOf(i).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const formatTime = (i) =>
  timeOf(i).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

const formatDuration = (i) => {
  const secondsRaw = i?.durationSeconds;
  if (secondsRaw == null) {
    // Legacy shape: some old records only stored a free-text "duration".
    return i?.duration || '';
  }
  const seconds = Math.max(0, Number(secondsRaw) || 0);
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  if (s === 0) return `${m}m`;
  return `${m}m ${s}s`;
};

const severityBadgeClasses = (intensity) => {
  if (!intensity) return 'bg-muted text-muted-foreground';
  if (intensity <= 2) return 'bg-success/10 text-success border border-success/30';
  if (intensity === 3) return 'bg-warning/10 text-warning border border-warning/30';
  return 'bg-destructive/10 text-destructive border border-destructive/30';
};

const severityLabel = (intensity) => {
  if (!intensity) return '—';
  if (intensity <= 2) return `Low · ${intensity}`;
  if (intensity === 3) return `Medium · 3`;
  return `High · ${intensity}`;
};

/**
 * IncidentReportTable — a dense, scannable report of every behavior
 * incident for the selected student. This exists alongside the timeline
 * (card view) and analytics (pie / bar / line charts) because teachers
 * and case managers routinely need to *read the actual notes* — not just
 * see aggregate counts. A pie of "40% attention-maintained" doesn't tell
 * you which Tuesday recess Sam pushed a peer or what the paraprofessional
 * wrote about it; this table does.
 *
 * Design goals:
 *  - One row per incident, all fields visible at a glance where possible.
 *  - Expandable row for the fields that don't fit (behavior description,
 *    antecedent notes, teacher notes, linked goals) — always accessible,
 *    never hidden behind a modal.
 *  - Same filter vocabulary as the timeline so switching tabs feels
 *    consistent.
 *  - Free-text search across description + notes so a teacher can pull up
 *    "the day I mentioned the fire drill" in one query.
 *  - CSV export using the same friendly labels the table displays, so a
 *    case manager can hand the file to an IEP team without needing a
 *    codebook.
 */
const IncidentReportTable = ({ incidents = [], selectedStudent }) => {
  const [filterType, setFilterType] = useState('all');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [filterFunction, setFilterFunction] = useState('all');
  const [filterFollowUp, setFilterFollowUp] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [search, setSearch] = useState('');
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const { getUserById } = useUserProfiles();

  // Pre-compute reporter display names once so CSV export and row render
  // stay in sync (and so we don't call into the profile lookup for every
  // sort compare).
  const enrichedIncidents = useMemo(() => {
    return (incidents || []).map((incident) => {
      const reporter = getUserById(incident?.reportedBy);
      const reporterName =
        reporter?.displayName ||
        deriveNameFromEmail(reporter?.email) ||
        (incident?.reportedBy ? 'A teammate' : '');
      return {
        ...incident,
        _reporter: reporter,
        _reporterName: reporterName,
      };
    });
  }, [incidents, getUserById]);

  const filteredIncidents = useMemo(() => {
    const query = search.trim().toLowerCase();
    const filtered = enrichedIncidents.filter((incident) => {
      if (filterType !== 'all' && incident?.behaviorType !== filterType) return false;
      if (filterSeverity !== 'all') {
        const severity = incident?.intensity ?? 0;
        if (filterSeverity === 'low' && severity > 2) return false;
        if (filterSeverity === 'medium' && severity !== 3) return false;
        if (filterSeverity === 'high' && severity < 4) return false;
      }
      if (filterFunction !== 'all' && incident?.hypothesizedFunction !== filterFunction) {
        return false;
      }
      if (filterFollowUp === 'yes' && !incident?.followUpRequired) return false;
      if (filterFollowUp === 'no' && incident?.followUpRequired) return false;
      if (query) {
        const haystack = [
          incident?.behaviorDescription,
          incident?.notes,
          incident?.antecedentNotes,
          humanize('behavior', incident?.behaviorType),
          humanize('antecedent', incident?.antecedent),
          humanize('consequence', incident?.consequence),
          humanize('location', incident?.location),
          humanize('setting', incident?.settingActivity),
          humanize('intervention', incident?.interventionUsed),
          incident?._reporterName,
          FUNCTION_BY_VALUE[incident?.hypothesizedFunction]?.label,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });

    return [...filtered].sort((a, b) => {
      switch (sortOrder) {
        case 'oldest':
          return timeOf(a) - timeOf(b);
        case 'severity-desc':
          return (b?.intensity ?? 0) - (a?.intensity ?? 0);
        case 'severity-asc':
          return (a?.intensity ?? 0) - (b?.intensity ?? 0);
        case 'duration-desc':
          return (b?.durationSeconds ?? 0) - (a?.durationSeconds ?? 0);
        default:
          return timeOf(b) - timeOf(a);
      }
    });
  }, [
    enrichedIncidents,
    filterType,
    filterSeverity,
    filterFunction,
    filterFollowUp,
    sortOrder,
    search,
  ]);

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allExpanded =
    filteredIncidents.length > 0 && filteredIncidents.every((r) => expandedIds.has(r.id));

  const toggleAllExpanded = () => {
    if (allExpanded) {
      setExpandedIds(new Set());
    } else {
      setExpandedIds(new Set(filteredIncidents.map((r) => r.id)));
    }
  };

  const studentDisplayName = selectedStudent?.firstName
    ? `${selectedStudent.firstName} ${selectedStudent.lastName ?? ''}`.trim()
    : selectedStudent?.name || 'Selected student';

  const totalCount = enrichedIncidents.length;
  const filteredCount = filteredIncidents.length;

  const hasAnyIncidents = totalCount > 0;
  const withNotesCount = filteredIncidents.filter(
    (r) => (r?.notes && r.notes.trim().length > 0) || (r?.antecedentNotes && r.antecedentNotes.trim().length > 0),
  ).length;

  const handleExport = () => {
    if (filteredCount === 0) return;
    downloadIncidentsCsv(filteredIncidents, studentDisplayName);
  };

  const handlePrint = () => {
    if (typeof window !== 'undefined') window.print();
  };

  const resetFilters = () => {
    setFilterType('all');
    setFilterSeverity('all');
    setFilterFunction('all');
    setFilterFollowUp('all');
    setSortOrder('newest');
    setSearch('');
  };

  return (
    <div className="bg-card rounded-lg border border-border p-4 sm:p-6">
      <div className="flex flex-col gap-4 mb-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-card-foreground">Behavior Data Table</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Every logged incident for{' '}
            <span className="font-medium">{studentDisplayName}</span> — with notes, ABC detail,
            and follow-up status. Use this when you need the story behind the numbers.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Showing <span className="font-medium">{filteredCount}</span> of {totalCount} incidents
            {withNotesCount > 0 && (
              <>
                {' '}
                · <span className="font-medium">{withNotesCount}</span> with notes
              </>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={toggleAllExpanded}
            disabled={filteredCount === 0}
          >
            <Icon name={allExpanded ? 'ChevronsDownUp' : 'ChevronsUpDown'} size={16} />
            {allExpanded ? 'Collapse all' : 'Expand all'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            disabled={filteredCount === 0}
          >
            <Icon name="Printer" size={16} />
            Print
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={filteredCount === 0}
          >
            <Icon name="Download" size={16} />
            Export CSV
          </Button>
        </div>
      </div>

      {hasAnyIncidents && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 mb-4">
          <div className="lg:col-span-2">
            <Input
              label="Search notes & description"
              type="search"
              placeholder="e.g. fire drill, math worksheet, hallway"
              value={search}
              onChange={(e) => setSearch(e?.target?.value ?? '')}
            />
          </div>
          <Select
            label="Behavior"
            options={behaviorTypeFilters}
            value={filterType}
            onChange={setFilterType}
            searchable
          />
          <Select
            label="Severity"
            options={severityFilters}
            value={filterSeverity}
            onChange={setFilterSeverity}
          />
          <Select
            label="Function"
            options={functionFilters}
            value={filterFunction}
            onChange={setFilterFunction}
          />
          <Select
            label="Follow-up"
            options={followUpFilters}
            value={filterFollowUp}
            onChange={setFilterFollowUp}
          />
          <div className="md:col-span-2 lg:col-span-6 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Sort by
              </label>
              <div className="min-w-52">
                <Select options={sortOptions} value={sortOrder} onChange={setSortOrder} />
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              <Icon name="RotateCcw" size={14} />
              Reset filters
            </Button>
          </div>
        </div>
      )}

      {!hasAnyIncidents ? (
        <div className="text-center py-12 border border-dashed border-border rounded-lg">
          <Icon name="Table" size={48} className="text-muted-foreground mx-auto mb-3 opacity-50" />
          <p className="text-sm text-muted-foreground">
            No incidents have been logged for {studentDisplayName} yet.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Log an incident on the "Log Incident" tab to see it appear here.
          </p>
        </div>
      ) : filteredCount === 0 ? (
        <div className="text-center py-10 border border-dashed border-border rounded-lg">
          <Icon
            name="SearchX"
            size={40}
            className="text-muted-foreground mx-auto mb-3 opacity-60"
          />
          <p className="text-sm text-muted-foreground">
            No incidents match the current filters.
          </p>
          <Button variant="link" size="sm" onClick={resetFilters}>
            Clear filters
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto border border-border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th scope="col" className="p-3 w-10" aria-label="Expand row" />
                <th scope="col" className="p-3 whitespace-nowrap">When</th>
                <th scope="col" className="p-3">Behavior</th>
                <th scope="col" className="p-3">Where</th>
                <th scope="col" className="p-3">Antecedent → Consequence</th>
                <th scope="col" className="p-3">Function</th>
                <th scope="col" className="p-3 whitespace-nowrap">Intensity</th>
                <th scope="col" className="p-3 whitespace-nowrap">Duration</th>
                <th scope="col" className="p-3">Intervention</th>
                <th scope="col" className="p-3">Notes</th>
                <th scope="col" className="p-3 whitespace-nowrap">Follow-up</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredIncidents.map((incident) => {
                const id = incident?.id;
                const isExpanded = expandedIds.has(id);
                const fn = incident?.hypothesizedFunction
                  ? FUNCTION_BY_VALUE[incident.hypothesizedFunction]
                  : null;
                const reporter = incident?._reporter;
                const reporterName = incident?._reporterName;
                const reporterShort = shortRoleLabel(reporter);
                const reporterColor = roleColorClasses(reporter);
                const behaviorLabel =
                  humanize('behavior', incident?.behaviorType) || '—';
                const antecedentLabel =
                  humanize('antecedent', incident?.antecedent) || '—';
                const consequenceLabel =
                  humanize('consequence', incident?.consequence) || '—';
                const locationLabel = humanize('location', incident?.location) || '—';
                const settingLabel = humanize('setting', incident?.settingActivity);
                const interventionLabel =
                  humanize('intervention', incident?.interventionUsed) || '—';
                const notes = (incident?.notes || '').trim();
                const antecedentNotes = (incident?.antecedentNotes || '').trim();
                const description = (incident?.behaviorDescription || '').trim();
                const staff = Array.isArray(incident?.staffInvolved)
                  ? incident.staffInvolved
                  : [];
                const linkedGoalCount = Array.isArray(incident?.linkedGoalIds)
                  ? incident.linkedGoalIds.length
                  : 0;
                const hasExtraDetail =
                  description.length > 0 ||
                  notes.length > 0 ||
                  antecedentNotes.length > 0 ||
                  staff.length > 0 ||
                  linkedGoalCount > 0 ||
                  Boolean(incident?.studentReaction) ||
                  Boolean(settingLabel);

                return (
                  <React.Fragment key={id}>
                    <tr
                      className={`align-top ${
                        isExpanded ? 'bg-muted/40' : 'hover:bg-muted/30'
                      } transition-colors`}
                    >
                      <td className="p-2">
                        <button
                          type="button"
                          onClick={() => toggleExpanded(id)}
                          aria-expanded={isExpanded}
                          aria-label={
                            isExpanded ? 'Collapse incident detail' : 'Expand incident detail'
                          }
                          className="p-1 rounded hover:bg-muted/60 text-muted-foreground"
                          disabled={!hasExtraDetail}
                          title={
                            hasExtraDetail
                              ? 'Show full description, notes, and staff'
                              : 'No extra detail recorded'
                          }
                        >
                          <Icon
                            name={isExpanded ? 'ChevronDown' : 'ChevronRight'}
                            size={16}
                            className={hasExtraDetail ? '' : 'opacity-30'}
                          />
                        </button>
                      </td>
                      <td className="p-3 whitespace-nowrap">
                        <div className="font-medium text-card-foreground">
                          {formatDate(incident)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatTime(incident)}
                        </div>
                      </td>
                      <td className="p-3 min-w-40">
                        <div className="font-medium text-card-foreground">
                          {behaviorLabel}
                        </div>
                        {description && (
                          <div className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {description}
                          </div>
                        )}
                      </td>
                      <td className="p-3 min-w-32">
                        <div className="text-card-foreground">{locationLabel}</div>
                        {settingLabel && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {settingLabel}
                          </div>
                        )}
                      </td>
                      <td className="p-3 min-w-56">
                        <div className="text-xs">
                          <span className="inline-block font-semibold text-muted-foreground mr-1">
                            A:
                          </span>
                          <span className="text-card-foreground">{antecedentLabel}</span>
                        </div>
                        <div className="text-xs mt-1">
                          <span className="inline-block font-semibold text-muted-foreground mr-1">
                            C:
                          </span>
                          <span className="text-card-foreground">{consequenceLabel}</span>
                        </div>
                      </td>
                      <td className="p-3 min-w-28">
                        {fn ? (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border"
                            style={{
                              borderColor: fn.color,
                              color: fn.color,
                              backgroundColor: `${fn.color}14`,
                            }}
                          >
                            {fn.label}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${severityBadgeClasses(
                            incident?.intensity,
                          )}`}
                        >
                          {severityLabel(incident?.intensity)}
                        </span>
                      </td>
                      <td className="p-3 whitespace-nowrap text-card-foreground">
                        {formatDuration(incident) || (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-3 min-w-40 text-card-foreground">
                        {interventionLabel !== '—' ? (
                          <span className="capitalize">{interventionLabel}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="p-3 min-w-56 max-w-xs">
                        {notes ? (
                          <p
                            className={
                              isExpanded
                                ? 'text-card-foreground whitespace-pre-wrap'
                                : 'text-card-foreground line-clamp-2'
                            }
                            title={isExpanded ? undefined : notes}
                          >
                            {notes}
                          </p>
                        ) : (
                          <span className="text-xs text-muted-foreground italic">
                            No notes recorded
                          </span>
                        )}
                      </td>
                      <td className="p-3 whitespace-nowrap">
                        {incident?.followUpRequired ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-warning/10 text-warning border border-warning/30">
                            <Icon name="Flag" size={12} />
                            Needed
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-muted/20">
                        <td />
                        <td colSpan={10} className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            {description && (
                              <div className="md:col-span-2">
                                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                                  Full behavior description
                                </p>
                                <p className="text-card-foreground whitespace-pre-wrap">
                                  {description}
                                </p>
                              </div>
                            )}
                            {antecedentNotes && (
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                                  Antecedent notes
                                </p>
                                <p className="text-card-foreground whitespace-pre-wrap">
                                  {antecedentNotes}
                                </p>
                              </div>
                            )}
                            {notes && (
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                                  Teacher notes
                                </p>
                                <p className="text-card-foreground whitespace-pre-wrap">
                                  {notes}
                                </p>
                              </div>
                            )}
                            {incident?.studentReaction && (
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                                  Student reaction
                                </p>
                                <p className="text-card-foreground">
                                  {humanize('studentReaction', incident.studentReaction)}
                                </p>
                              </div>
                            )}
                            {settingLabel && (
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                                  Setting / activity
                                </p>
                                <p className="text-card-foreground">{settingLabel}</p>
                              </div>
                            )}
                            {staff.length > 0 && (
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                                  Staff involved
                                </p>
                                <div className="flex flex-wrap gap-1.5">
                                  {staff.map((role) => (
                                    <span
                                      key={role}
                                      className="inline-block px-2 py-0.5 rounded-full text-xs bg-muted text-card-foreground capitalize"
                                    >
                                      {String(role).replace(/-/g, ' ')}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {linkedGoalCount > 0 && (
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                                  Linked IEP goals
                                </p>
                                <p className="text-card-foreground">
                                  {linkedGoalCount === 1
                                    ? '1 goal linked'
                                    : `${linkedGoalCount} goals linked`}
                                </p>
                              </div>
                            )}
                            {reporterName && (
                              <div>
                                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
                                  Reported by
                                </p>
                                <p className="text-card-foreground flex items-center gap-2 flex-wrap">
                                  <span>{reporterName}</span>
                                  {reporterShort && (
                                    <span
                                      className={`inline-block text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${reporterColor}`}
                                    >
                                      {reporterShort}
                                    </span>
                                  )}
                                </p>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default IncidentReportTable;
